import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Popover, Button, Tooltip } from 'antd';
import { TranslationOutlined } from '@ant-design/icons';
import './style.scss';

/**
 * Component that displays a language selector.
 *
 * @returns {JSX.Element} The LanguageSelector component.
 * @example
 * <LanguageSelector />
 */
const LanguageSelector = () => {
  const { i18n, t } = useTranslation();

  const [selectedLanguage, setSelectedLanguage] = useState(i18n.language);
  const [popoverVisible, setPopoverVisible] = useState(false);
  const alignButtonsRef = useRef(null);
  const popoverRef = useRef(null);

  /**
   * Event handler for changing the language.
   * @param {string} language - The selected language code.
   */
  const handleLanguageChange = (language) => {
    i18n.changeLanguage(language);
    window.localStorage.setItem('language', language);
    setSelectedLanguage(language); // Update the selected language after the change
    setPopoverVisible(false); // Close the popover after changing the language
  };

  /**
   * Handles changes to the visibility of the popover.
   *
   * @param {boolean} visible - Whether the popover is visible or not.
   * @returns {void}
   */
  const handlePopoverVisibleChange = (visible) => {
    setPopoverVisible(visible);
    if (visible && popoverRef.current instanceof HTMLElement) {
      popoverRef.current.focus();
    }
  };

  /**
   * Handles a key down event to create a shortcut for the translation.
   * @param {KeyboardEvent} event - The key down event.
   */
  const handleKeyDown = (event) => {
    if (event.key === 'F2') {
      event.preventDefault();
      if (alignButtonsRef.current) {
        alignButtonsRef.current.focus();
      }
      setPopoverVisible(true);
    } else if (event.key === 'Escape') {
      setPopoverVisible(false);
      document.body.focus();
    }
  };

  const content = (
    <div className="align-translation" tabIndex={0} ref={alignButtonsRef}>
      <Button
        type={selectedLanguage === 'en' ? 'primary' : 'default'}
        onClick={() => handleLanguageChange('en')}
        role="option"
        aria-selected={selectedLanguage === 'en'}
        aria-label="English"
        tabIndex={0}
      >
        English
      </Button>
      <Button
        type={selectedLanguage === 'pt-BR' ? 'primary' : 'default'}
        onClick={() => handleLanguageChange('pt-BR')}
        role="option"
        aria-selected={selectedLanguage === 'pt-BR'}
        aria-label="Português"
        tabIndex={0}
      >
        Portuguese
      </Button>
    </div>
  );

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  return (
    <Tooltip title={t('translateTooltip')} arrow={false}>
      <Popover
        content={content}
        trigger="click"
        tabIndex={0}
        visible={popoverVisible}
        onVisibleChange={handlePopoverVisibleChange}
        overlayClassName="popover-overlay"
        ref={popoverRef}
      >
        <Button
          tabIndex={0}
          aria-label={t('translateButton')}
          role="button"
          title={t('translateTooltip')}
          className="translate-button"
        >
          <TranslationOutlined style={{ fontSize: 20, cursor: 'pointer', marginRight: '0px' }} />
        </Button>
      </Popover>
    </Tooltip>
  );
};

export default LanguageSelector;
